import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../../lib/i18n';
import Drawer from '../ui/Drawer';
import Button from '../ui/Button';
import Toggle from '../ui/Toggle';
import ConsentNoteDialog from './ConsentNoteDialog';
import {
  Subscriber,
  subscriberSetFlags,
  subscriberBulk,
  subscriberAssignAgent,
  subscriberNoteAdd,
  subscriberNoteDelete,
  updateSubscriber,
} from '../../lib/subscribers';
import { useLabels, useLists, useCustomFields } from '../../lib/crm';
import { useTeam } from '../../lib/team';
import { useSequences } from '../../lib/sequences';
import { inputFlowReset } from '../../lib/inputFlows';
import {
  InboxIcon,
  TrashIcon,
  CheckIcon,
  HelpCircleIcon,
} from '../icons';

interface SubscriberDrawerProps {
  open: boolean;
  onClose: () => void;
  subscriber: Subscriber | null;
  onUpdated?: () => void;
  onOpenLabelsManager?: () => void;
  onOpenListsManager?: () => void;
  onOpenCustomFieldsManager?: () => void;
}

type TabKey = 'overview' | 'agent' | 'labels' | 'lists' | 'sequences' | 'customFields' | 'notes';

export default function SubscriberDrawer({
  open,
  onClose,
  subscriber,
  onUpdated,
  onOpenLabelsManager,
  onOpenListsManager,
  onOpenCustomFieldsManager,
}: SubscriberDrawerProps) {
  const { t } = useI18n();
  const { labels } = useLabels();
  const { lists } = useLists();
  const { customFields } = useCustomFields();
  const { members } = useTeam();
  const { sequences } = useSequences();

  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Consent note dialog state for resubscribe
  const [showConsentDialog, setShowConsentDialog] = useState(false);

  // Custom fields form state
  const [cfForm, setCfForm] = useState<Record<string, any>>({});

  // Note input state
  const [newNoteText, setNewNoteText] = useState('');

  // Selected sequence for enrollment
  const [selectedSequenceId, setSelectedSequenceId] = useState('');

  // Sync custom fields from subscriber when opened
  useEffect(() => {
    if (subscriber) {
      setCfForm(subscriber.customFields || {});
      setError(null);
      setSuccessMsg(null);
    }
  }, [subscriber]);

  if (!subscriber) return null;

  const showFeedback = (msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 3500);
  };

  // Status badge styling
  const status = subscriber.optIn?.status || subscriber.status || 'pending';
  const statusBadge =
    status === 'subscribed' ? (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
        {t('subs.status.subscribed') || 'Subscribed'}
      </span>
    ) : status === 'unsubscribed' ? (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold bg-rose-50 text-rose-700 border border-rose-200">
        {t('subs.status.unsubscribed') || 'Opted Out'}
      </span>
    ) : (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
        {t('subs.status.pending') || 'Pending'}
      </span>
    );

  // Quick Action Handlers
  const handleToggleBot = async () => {
    setBusy(true);
    setError(null);
    try {
      await subscriberSetFlags({
        subscriberId: subscriber.id,
        botPaused: !subscriber.botPaused,
      });
      subscriber.botPaused = !subscriber.botPaused;
      showFeedback(subscriber.botPaused ? 'Bot automation paused' : 'Bot automation resumed');
      onUpdated?.();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const handleToggleAi = async () => {
    setBusy(true);
    setError(null);
    try {
      await subscriberSetFlags({
        subscriberId: subscriber.id,
        aiPaused: !subscriber.aiPaused,
      });
      subscriber.aiPaused = !subscriber.aiPaused;
      showFeedback(subscriber.aiPaused ? 'AI replies paused' : 'AI replies resumed');
      onUpdated?.();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const handleUnsubscribe = async () => {
    setBusy(true);
    setError(null);
    try {
      await subscriberBulk({
        ids: [subscriber.id],
        action: 'unsubscribe',
      });
      subscriber.status = 'unsubscribed';
      if (subscriber.optIn) subscriber.optIn.status = 'unsubscribed';
      showFeedback('Subscriber unsubscribed');
      onUpdated?.();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const handleResubscribeConfirm = async (consentNote: string) => {
    setBusy(true);
    setError(null);
    try {
      await subscriberBulk({
        ids: [subscriber.id],
        action: 'resubscribe',
        consentNote,
      });
      subscriber.status = 'subscribed';
      if (subscriber.optIn) {
        subscriber.optIn.status = 'subscribed';
        subscriber.optIn.consentNote = consentNote;
      }
      setShowConsentDialog(false);
      showFeedback('Subscriber resubscribed with consent note');
      onUpdated?.();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const handleResetFlow = async () => {
    setBusy(true);
    setError(null);
    try {
      await inputFlowReset({ subscriberId: subscriber.id });
      subscriber.currentFlowId = null;
      subscriber.currentStepId = null;
      showFeedback('Input flow reset successfully');
      onUpdated?.();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const handleClearHistory = async () => {
    if (!confirm('Are you sure you want to clear message history for this subscriber?')) return;
    setBusy(true);
    setError(null);
    try {
      await subscriberBulk({
        ids: [subscriber.id],
        action: 'clearHistory',
      });
      showFeedback('Conversation history cleared');
      onUpdated?.();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Agent Assignment
  const handleAssignAgent = async (agentUid: string | null) => {
    setBusy(true);
    setError(null);
    try {
      await subscriberAssignAgent({
        subscriberId: subscriber.id,
        agentUid,
      });
      subscriber.agentUid = agentUid;
      showFeedback('Agent assignment updated');
      onUpdated?.();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Label Toggle
  const handleToggleLabel = async (labelId: string, hasLabel: boolean) => {
    setBusy(true);
    setError(null);
    try {
      await subscriberBulk({
        ids: [subscriber.id],
        action: hasLabel ? 'removeLabel' : 'addLabel',
        labelId,
      });
      if (hasLabel) {
        subscriber.labelIds = subscriber.labelIds.filter((id) => id !== labelId);
      } else {
        subscriber.labelIds = [...subscriber.labelIds, labelId];
      }
      onUpdated?.();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // List Toggle
  const handleToggleList = async (listId: string, inList: boolean) => {
    setBusy(true);
    setError(null);
    try {
      await subscriberBulk({
        ids: [subscriber.id],
        action: inList ? 'removeList' : 'addList',
        listId,
      });
      if (inList) {
        subscriber.listIds = subscriber.listIds.filter((id) => id !== listId);
      } else {
        subscriber.listIds = [...subscriber.listIds, listId];
      }
      onUpdated?.();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Sequence Enrollment
  const handleEnrollSequence = async () => {
    if (!selectedSequenceId) return;
    setBusy(true);
    setError(null);
    try {
      await subscriberBulk({
        ids: [subscriber.id],
        action: 'assignSequence',
        sequenceId: selectedSequenceId,
      });
      if (!subscriber.sequenceIds) subscriber.sequenceIds = [];
      if (!subscriber.sequenceIds.includes(selectedSequenceId)) {
        subscriber.sequenceIds.push(selectedSequenceId);
      }
      setSelectedSequenceId('');
      showFeedback('Enrolled into sequence successfully');
      onUpdated?.();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Custom Fields Save
  const handleSaveCustomFields = async () => {
    setBusy(true);
    setError(null);
    try {
      await updateSubscriber({
        subscriberId: subscriber.id,
        customFields: cfForm,
      });
      subscriber.customFields = { ...cfForm };
      showFeedback('Custom fields saved');
      onUpdated?.();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Notes
  const handleAddNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newNoteText.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const res = await subscriberNoteAdd({
        subscriberId: subscriber.id,
        text: newNoteText.trim(),
      });
      if (!subscriber.notes) subscriber.notes = [];
      subscriber.notes.unshift(res.note);
      setNewNoteText('');
      showFeedback('Note added');
      onUpdated?.();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    setBusy(true);
    setError(null);
    try {
      await subscriberNoteDelete({
        subscriberId: subscriber.id,
        noteId,
      });
      if (subscriber.notes) {
        subscriber.notes = subscriber.notes.filter((n) => n.id !== noteId);
      }
      showFeedback('Note deleted');
      onUpdated?.();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const formatDate = (millis?: number | null) => {
    if (!millis) return '—';
    return new Date(millis).toLocaleString();
  };

  const assignedMember = members.find((m) => m.uid === subscriber.agentUid);

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        size="xl"
        title={
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-indigo-500 to-primary-600 text-white font-bold flex items-center justify-center text-sm">
              {(subscriber.name || subscriber.phoneE164 || 'S').slice(0, 2).toUpperCase()}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-base font-bold text-slate-900">
                  {subscriber.name || t('subs.unnamed') || 'Unnamed Contact'}
                </span>
                {statusBadge}
              </div>
              <div className="text-xs font-mono text-slate-500">{subscriber.phoneE164}</div>
            </div>
          </div>
        }
      >
        <div className="space-y-6">
          {/* Quick Actions Bar */}
          <div className="flex flex-wrap items-center gap-2 p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs">
            {/* Open In Inbox */}
            <Link
              to={`/inbox?phone=${subscriber.phoneE164.replace(/\+/g, '')}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-semibold border border-indigo-200 transition-colors"
            >
              <InboxIcon size={14} />
              <span>{t('subs.quick.openInbox') || 'Open in Inbox'}</span>
            </Link>

            {/* Pause Bot Toggle */}
            <button
              type="button"
              onClick={handleToggleBot}
              disabled={busy}
              className={`px-3 py-1.5 rounded-lg border font-medium transition-colors ${
                subscriber.botPaused
                  ? 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'
                  : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100'
              }`}
            >
              {subscriber.botPaused
                ? t('subs.quick.botPaused') || 'Bot: Paused'
                : t('subs.quick.botActive') || 'Bot: Active'}
            </button>

            {/* Pause AI Toggle */}
            <button
              type="button"
              onClick={handleToggleAi}
              disabled={busy}
              className={`px-3 py-1.5 rounded-lg border font-medium transition-colors ${
                subscriber.aiPaused
                  ? 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100'
                  : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100'
              }`}
            >
              {subscriber.aiPaused
                ? t('subs.quick.aiPaused') || 'AI: Paused'
                : t('subs.quick.aiActive') || 'AI: Active'}
            </button>

            {/* Unsubscribe / Resubscribe */}
            {status === 'subscribed' ? (
              <button
                type="button"
                onClick={handleUnsubscribe}
                disabled={busy}
                className="px-3 py-1.5 rounded-lg bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 font-medium transition-colors"
              >
                {t('subs.quick.unsubscribe') || 'Unsubscribe'}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setShowConsentDialog(true)}
                disabled={busy}
                className="px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 font-medium transition-colors"
              >
                {t('subs.quick.resubscribe') || 'Resubscribe'}
              </button>
            )}

            {/* Reset Input Flow */}
            <button
              type="button"
              onClick={handleResetFlow}
              disabled={busy}
              className="px-3 py-1.5 rounded-lg bg-white text-slate-600 border border-slate-200 hover:bg-slate-100 font-medium transition-colors"
            >
              {t('subs.quick.resetFlow') || 'Reset Flow'}
            </button>

            {/* Clear History */}
            <button
              type="button"
              onClick={handleClearHistory}
              disabled={busy}
              className="px-3 py-1.5 rounded-lg bg-white text-rose-600 border border-rose-200 hover:bg-rose-50 font-medium transition-colors"
            >
              {t('subs.quick.clearHistory') || 'Clear History'}
            </button>
          </div>

          {/* Feedback messages */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-xs flex items-center gap-2">
              <HelpCircleIcon size={16} className="shrink-0 text-red-500" />
              <span>{error}</span>
            </div>
          )}
          {successMsg && (
            <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg text-xs flex items-center gap-2">
              <CheckIcon size={16} className="shrink-0 text-emerald-500" />
              <span>{successMsg}</span>
            </div>
          )}

          {/* Drawer Navigation Tabs */}
          <div className="flex border-b border-slate-200 overflow-x-auto">
            <button
              type="button"
              onClick={() => setActiveTab('overview')}
              className={`pb-2.5 px-3 text-xs font-semibold whitespace-nowrap border-b-2 transition-colors ${
                activeTab === 'overview'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {t('subs.drawer.overview') || 'Overview'}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('agent')}
              className={`pb-2.5 px-3 text-xs font-semibold whitespace-nowrap border-b-2 transition-colors ${
                activeTab === 'agent'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {t('subs.drawer.agent') || 'Agent'}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('labels')}
              className={`pb-2.5 px-3 text-xs font-semibold whitespace-nowrap border-b-2 transition-colors ${
                activeTab === 'labels'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {t('subs.drawer.labels') || 'Labels'} ({subscriber.labelIds?.length || 0})
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('lists')}
              className={`pb-2.5 px-3 text-xs font-semibold whitespace-nowrap border-b-2 transition-colors ${
                activeTab === 'lists'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {t('subs.drawer.lists') || 'Lists'} ({subscriber.listIds?.length || 0})
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('sequences')}
              className={`pb-2.5 px-3 text-xs font-semibold whitespace-nowrap border-b-2 transition-colors ${
                activeTab === 'sequences'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {t('subs.drawer.sequences') || 'Sequences'} ({subscriber.sequenceIds?.length || 0})
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('customFields')}
              className={`pb-2.5 px-3 text-xs font-semibold whitespace-nowrap border-b-2 transition-colors ${
                activeTab === 'customFields'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {t('subs.drawer.customFields') || 'Custom Fields'}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('notes')}
              className={`pb-2.5 px-3 text-xs font-semibold whitespace-nowrap border-b-2 transition-colors ${
                activeTab === 'notes'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {t('subs.drawer.notes') || 'Notes'} ({subscriber.notes?.length || 0})
            </button>
          </div>

          {/* TAB 1: OVERVIEW */}
          {activeTab === 'overview' && (
            <div className="space-y-4 text-xs">
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
                <div>
                  <span className="text-slate-400 font-medium block mb-0.5">{t('subs.col.contactPhone') || 'Phone Number'}</span>
                  <span className="font-semibold text-slate-800 font-mono">
                    {subscriber.phoneE164}
                  </span>
                </div>
                <div>
                  <span className="text-slate-400 font-medium block mb-0.5">{t('subs.field.name') || 'Full Name'}</span>
                  <span className="font-semibold text-slate-800">
                    {subscriber.name || '—'}
                  </span>
                </div>
                <div>
                  <span className="text-slate-400 font-medium block mb-0.5">{t('subs.field.email') || 'Email Address'}</span>
                  <span className="font-semibold text-slate-800">
                    {subscriber.email || '—'}
                  </span>
                </div>
                <div>
                  <span className="text-slate-400 font-medium block mb-0.5">{t('subs.col.countryTimezone') || 'Country & Timezone'}</span>
                  <span className="font-semibold text-slate-800">
                    {subscriber.country || '—'} {subscriber.timezone ? `(${subscriber.timezone})` : ''}
                  </span>
                </div>
                <div>
                  <span className="text-slate-400 font-medium block mb-0.5">{t('subs.col.preferredLanguage') || 'Preferred Language'}</span>
                  <span className="font-semibold text-slate-800 uppercase">
                    {subscriber.language || 'en'}
                  </span>
                </div>
                <div>
                  <span className="text-slate-400 font-medium block mb-0.5">{t('subs.col.channelSource') || 'Channel / Source'}</span>
                  <span className="font-semibold text-slate-800 capitalize">
                    {subscriber.channel || 'whatsapp'} / {subscriber.source || 'manual'}
                  </span>
                </div>
                <div>
                  <span className="text-slate-400 font-medium block mb-0.5">{t('subs.col.subscribedDate') || 'Subscribed Date'}</span>
                  <span className="font-semibold text-slate-800">
                    {formatDate(subscriber.subscribedAt || subscriber.createdAt)}
                  </span>
                </div>
                <div>
                  <span className="text-slate-400 font-medium block mb-0.5">{t('subs.col.subscribed') || 'Last Seen / Active'}</span>
                  <span className="font-semibold text-slate-800">
                    {formatDate(subscriber.lastSeenAt)}
                  </span>
                </div>
              </div>

              {/* Bot Flow state */}
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                <span className="text-slate-400 font-medium block mb-1">{t('subs.col.flowPosition') || 'Bot / Flow Position'}</span>
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-semibold text-slate-800">
                      Flow: {subscriber.currentFlowId || 'None'}
                    </span>
                    <span className="text-slate-500 ms-2">
                      (Step: {subscriber.currentStepId || '—'})
                    </span>
                  </div>
                  {subscriber.currentFlowId && (
                    <Button variant="secondary" size="sm" onClick={handleResetFlow} disabled={busy}>
                      {t('subs.quick.resetFlow') || 'Reset'}
                    </Button>
                  )}
                </div>
              </div>

              {/* Opt-In Audit details if available */}
              {subscriber.optIn && (
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <span className="text-slate-400 font-medium block mb-1">{t('subs.col.complianceInfo') || 'Compliance & Opt-In Info'}</span>
                  <div className="space-y-1 text-[11px] text-slate-600">
                    <div>{t('subs.col.optInStatus') || 'Status'}: <strong className="capitalize">{subscriber.optIn.status}</strong></div>
                    {subscriber.optIn.optOutSource && (
                      <div>{t('subs.col.optOutSource') || 'Opt-out Source'}: <strong>{subscriber.optIn.optOutSource}</strong></div>
                    )}
                    {subscriber.optIn.consentNote && (
                      <div>{t('subs.col.consentNote') || 'Consent Note'}: <em className="text-slate-800">"{subscriber.optIn.consentNote}"</em></div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB 2: AGENT ASSIGNMENT */}
          {activeTab === 'agent' && (
            <div className="space-y-4 text-xs">
              <div>
                <label className="block font-semibold text-slate-700 mb-1.5">
                  Assigned Team Member
                </label>
                <select
                  value={subscriber.agentUid || ''}
                  onChange={(e) => handleAssignAgent(e.target.value || null)}
                  disabled={busy}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs outline-none focus:border-indigo-500"
                >
                  <option value="">— Unassigned —</option>
                  {members.map((m) => (
                    <option key={m.uid} value={m.uid}>
                      {m.email} ({m.role})
                    </option>
                  ))}
                </select>
              </div>

              {assignedMember ? (
                <div className="p-4 bg-indigo-50/50 border border-indigo-100 rounded-xl flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-indigo-600 text-white font-bold flex items-center justify-center text-xs">
                      {assignedMember.email.slice(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900">{assignedMember.email}</div>
                      <div className="text-[11px] text-slate-500 capitalize">{assignedMember.role}</div>
                    </div>
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleAssignAgent(null)}
                    disabled={busy}
                  >
                    Unassign
                  </Button>
                </div>
              ) : (
                <div className="p-4 bg-slate-50 rounded-xl text-center text-slate-500">
                  This subscriber is currently unassigned. Any agent can respond to chats.
                </div>
              )}
            </div>
          )}

          {/* TAB 3: LABELS */}
          {activeTab === 'labels' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500">
                  Assign labels to segment and organize this contact.
                </span>
                {onOpenLabelsManager && (
                  <button
                    type="button"
                    onClick={onOpenLabelsManager}
                    className="text-xs font-semibold text-indigo-600 hover:text-indigo-700"
                  >
                    Manage Labels
                  </button>
                )}
              </div>

              {labels.length === 0 ? (
                <div className="p-6 text-center text-xs text-slate-400 bg-slate-50 rounded-xl">
                  No labels found. Click "Manage Labels" to create one.
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {labels.map((l) => {
                    const active = subscriber.labelIds?.includes(l.id);
                    return (
                      <button
                        key={l.id}
                        type="button"
                        onClick={() => handleToggleLabel(l.id, active)}
                        disabled={busy}
                        className={`flex items-center justify-between p-2.5 rounded-lg border text-xs text-start transition-all ${
                          active
                            ? 'border-indigo-300 bg-indigo-50/40 text-slate-900'
                            : 'border-slate-200 hover:border-slate-300 bg-white text-slate-700'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span
                            className="w-3 h-3 rounded-full shrink-0"
                            style={{ backgroundColor: l.color }}
                          />
                          <span className="font-semibold">{l.name}</span>
                        </div>
                        {active && <CheckIcon size={14} className="text-indigo-600 shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* TAB 4: LISTS */}
          {activeTab === 'lists' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500">
                  Add or remove this subscriber from broadcast subscriber lists.
                </span>
                {onOpenListsManager && (
                  <button
                    type="button"
                    onClick={onOpenListsManager}
                    className="text-xs font-semibold text-indigo-600 hover:text-indigo-700"
                  >
                    Manage Lists
                  </button>
                )}
              </div>

              {lists.length === 0 ? (
                <div className="p-6 text-center text-xs text-slate-400 bg-slate-50 rounded-xl">
                  No lists found. Click "Manage Lists" to create one.
                </div>
              ) : (
                <div className="space-y-2">
                  {lists.map((list) => {
                    const inList = subscriber.listIds?.includes(list.id);
                    return (
                      <div
                        key={list.id}
                        className={`flex items-center justify-between p-3 rounded-lg border text-xs transition-all ${
                          inList
                            ? 'border-indigo-300 bg-indigo-50/30 text-slate-900'
                            : 'border-slate-200 bg-white text-slate-700'
                        }`}
                      >
                        <div>
                          <div className="font-semibold">{list.name}</div>
                          {list.description && (
                            <div className="text-[11px] text-slate-400">{list.description}</div>
                          )}
                        </div>
                        <Button
                          variant={inList ? 'secondary' : 'primary'}
                          size="sm"
                          onClick={() => handleToggleList(list.id, inList)}
                          disabled={busy}
                        >
                          {inList ? 'Remove' : 'Add to List'}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* TAB 5: SEQUENCES */}
          {activeTab === 'sequences' && (
            <div className="space-y-4 text-xs">
              {/* Enrollment action */}
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
                <span className="font-semibold text-slate-800 block">
                  Enroll in Drip Sequence
                </span>
                <div className="flex gap-2">
                  <select
                    value={selectedSequenceId}
                    onChange={(e) => setSelectedSequenceId(e.target.value)}
                    className="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs outline-none focus:border-indigo-500"
                  >
                    <option value="">— Select a sequence —</option>
                    {sequences
                      .filter((s) => !subscriber.sequenceIds?.includes(s.id))
                      .map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name} ({s.steps.length} steps)
                        </option>
                      ))}
                  </select>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleEnrollSequence}
                    disabled={busy || !selectedSequenceId}
                  >
                    Enroll
                  </Button>
                </div>
              </div>

              {/* Currently enrolled list */}
              <div>
                <h5 className="font-semibold text-slate-700 mb-2">{t('subs.col.activeSequences') || 'Active Sequences'}</h5>
                {(!subscriber.sequenceIds || subscriber.sequenceIds.length === 0) ? (
                  <p className="text-slate-400">Not currently enrolled in any sequences.</p>
                ) : (
                  <div className="space-y-2">
                    {subscriber.sequenceIds.map((seqId) => {
                      const seq = sequences.find((s) => s.id === seqId);
                      return (
                        <div
                          key={seqId}
                          className="p-3 bg-white border border-slate-200 rounded-lg flex items-center justify-between"
                        >
                          <div>
                            <span className="font-semibold text-slate-900">
                              {seq ? seq.name : seqId}
                            </span>
                            {seq && (
                              <span className="text-slate-400 ms-2">
                                ({seq.steps.length} steps)
                              </span>
                            )}
                          </div>
                          <span className="text-[11px] font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded">
                            Enrolled
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TAB 6: CUSTOM FIELDS */}
          {activeTab === 'customFields' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500">
                  Custom parameters for variable replacement and segmentation.
                </span>
                {onOpenCustomFieldsManager && (
                  <button
                    type="button"
                    onClick={onOpenCustomFieldsManager}
                    className="text-xs font-semibold text-indigo-600 hover:text-indigo-700"
                  >
                    Manage Attributes
                  </button>
                )}
              </div>

              {customFields.length === 0 ? (
                <div className="p-6 text-center text-xs text-slate-400 bg-slate-50 rounded-xl">
                  No custom attributes defined. Click "Manage Attributes" to define custom fields.
                </div>
              ) : (
                <div className="space-y-3">
                  {customFields.map((cf) => {
                    const val = cfForm[cf.key] ?? '';

                    return (
                      <div key={cf.id} className="text-xs">
                        <label className="block font-semibold text-slate-700 mb-1">
                          {cf.label}{' '}
                          <span className="text-slate-400 font-mono font-normal">
                            ({cf.key})
                          </span>
                        </label>

                        {cf.type === 'boolean' ? (
                          <div className="pt-1">
                            <Toggle
                              on={Boolean(val)}
                              onChange={(next) =>
                                setCfForm({ ...cfForm, [cf.key]: next })
                              }
                              label={cf.label}
                            />
                          </div>
                        ) : cf.type === 'select' ? (
                          <select
                            value={String(val)}
                            onChange={(e) =>
                              setCfForm({ ...cfForm, [cf.key]: e.target.value })
                            }
                            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs outline-none focus:border-indigo-500"
                          >
                            <option value="">— Select —</option>
                            {cf.options?.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type={
                              cf.type === 'number'
                                ? 'number'
                                : cf.type === 'date'
                                ? 'date'
                                : 'text'
                            }
                            value={val}
                            onChange={(e) =>
                              setCfForm({
                                ...cfForm,
                                [cf.key]:
                                  cf.type === 'number'
                                    ? Number(e.target.value)
                                    : e.target.value,
                              })
                            }
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-xs outline-none focus:border-indigo-500"
                          />
                        )}
                      </div>
                    );
                  })}

                  <div className="pt-2 flex justify-end">
                    <Button
                      variant="primary"
                      onClick={handleSaveCustomFields}
                      disabled={busy}
                    >
                      Save Attributes
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB 7: NOTES */}
          {activeTab === 'notes' && (
            <div className="space-y-4 text-xs">
              {/* Add note form */}
              <form onSubmit={handleAddNote} className="space-y-2">
                <textarea
                  rows={3}
                  value={newNoteText}
                  onChange={(e) => setNewNoteText(e.target.value)}
                  placeholder={t('subs.notePlaceholder') || 'Add an internal note about this contact...'}
                  className="w-full rounded-lg border border-slate-200 p-3 text-xs outline-none focus:border-indigo-500"
                />
                <div className="flex justify-end">
                  <Button
                    type="submit"
                    variant="primary"
                    size="sm"
                    disabled={busy || !newNoteText.trim()}
                  >
                    Add Note
                  </Button>
                </div>
              </form>

              {/* Notes List */}
              <div className="space-y-3 pt-2">
                {(!subscriber.notes || subscriber.notes.length === 0) ? (
                  <p className="text-slate-400 text-center py-4">No notes recorded yet.</p>
                ) : (
                  subscriber.notes.map((note) => (
                    <div
                      key={note.id}
                      className="p-3 bg-slate-50 border border-slate-200 rounded-lg space-y-1 relative group"
                    >
                      <div className="flex items-center justify-between text-[11px] text-slate-400">
                        <span className="font-semibold text-slate-700">
                          {note.authorEmail || 'Staff'}
                        </span>
                        <span>{formatDate(note.createdAt)}</span>
                      </div>
                      <p className="text-slate-800 whitespace-pre-wrap">{note.text}</p>
                      <button
                        type="button"
                        onClick={() => handleDeleteNote(note.id)}
                        className="absolute top-2 end-2 p-1 text-slate-300 hover:text-rose-600 opacity-0 group-hover:opacity-100 transition-opacity"
                        title={t('subs.deleteNote') || 'Delete note'}
                      >
                        <TrashIcon size={14} />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </Drawer>

      {/* Compliance Consent Note Dialog for Resubscribe */}
      <ConsentNoteDialog
        isOpen={showConsentDialog}
        onClose={() => setShowConsentDialog(false)}
        onConfirm={handleResubscribeConfirm}
        targetCount={1}
      />
    </>
  );
}
